import React from "react";

/**
 * Zero-dependency SVG sparkline. Renders a smooth trend line, an area
 * fill, and light gridlines. Nulls (no data on that day) break the path
 * so gaps read naturally.
 *
 * Feb 2026 (V1.3): powers the Growth-dashboard "Response rate · last 30d"
 * chart for the reference-check module.
 */
export default function Sparkline({
    points = [],           // Array<{date: string, value: number|null, meta?: any}>
    width = 320,
    height = 60,
    stroke = "#0A0A0A",
    fill = "rgba(10,10,10,0.08)",
    padding = 4,
    testid,
}) {
    const filtered = points.filter((p) => p && Number.isFinite(p.value));
    if (filtered.length === 0) {
        return (
            <div
                data-testid={testid}
                className="flex items-center justify-center text-[10px] text-[#A3A3A3] italic"
                style={{ width, height }}
            >
                No data yet
            </div>
        );
    }
    const maxV = Math.max(...filtered.map((p) => p.value), 100); // response rate is 0-100
    const minV = 0;
    const w = width - padding * 2;
    const h = height - padding * 2;
    const stepX = points.length > 1 ? w / (points.length - 1) : 0;

    // Build path (M/L segments, skipping nulls).
    let path = "";
    let inSegment = false;
    const coords = points.map((p, i) => {
        const x = padding + i * stepX;
        if (p && Number.isFinite(p.value)) {
            const y = padding + h - ((p.value - minV) / Math.max(1, maxV - minV)) * h;
            const cmd = inSegment ? "L" : "M";
            path += `${cmd}${x.toFixed(1)},${y.toFixed(1)} `;
            inSegment = true;
            return { x, y, val: p.value, date: p.date };
        }
        inSegment = false;
        return null;
    });

    // Build area path — mirror the last segment back to the baseline.
    let area = "";
    const validCoords = coords.filter(Boolean);
    if (validCoords.length >= 2) {
        area = "M" + validCoords[0].x.toFixed(1) + "," + (padding + h).toFixed(1) + " ";
        validCoords.forEach((c) => {
            area += `L${c.x.toFixed(1)},${c.y.toFixed(1)} `;
        });
        area += `L${validCoords[validCoords.length - 1].x.toFixed(1)},${(padding + h).toFixed(1)} Z`;
    }

    return (
        <svg
            data-testid={testid}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Response rate trend line"
        >
            {/* horizontal midline at 50% for context */}
            <line
                x1={padding}
                x2={padding + w}
                y1={padding + h * 0.5}
                y2={padding + h * 0.5}
                stroke="#E5E5E5"
                strokeDasharray="2,3"
                strokeWidth="1"
            />
            {area && <path d={area} fill={fill} />}
            <path d={path.trim()} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
            {/* end-dot to anchor the eye */}
            {validCoords.length > 0 && (
                <circle
                    cx={validCoords[validCoords.length - 1].x}
                    cy={validCoords[validCoords.length - 1].y}
                    r={2.5}
                    fill={stroke}
                />
            )}
        </svg>
    );
}
